import {
    Alert, AlertProps, Slide, Typography
} from '@mui/material';
import clsx from 'clsx';
import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';

import { isApiError, isValidationApiError } from '../../utils';

import styles from './PageMessage.module.scss';


interface PageMessageProps {
    className?: string;
    dialog?: boolean;
}

export function PageMessage({ className, dialog }: PageMessageProps) {
    const {
        message, subMessages, severity, dialogOpen, setPageMessage
    } = useContext(PageMessageContext);

    const hasMessage = message !== undefined;

    return (
        <div
            className={clsx(className, {
                [styles.hidden]: !hasMessage
            })}
        >
            <Slide
                direction="right"
                in={hasMessage && (!dialogOpen || dialog)}
            >
                <Alert
                    onClose={() => setPageMessage(undefined)}
                    severity={severity}
                >
                    {!subMessages?.length ? message : (
                        <>
                            <Typography
                                fontWeight={500}
                                fontSize="inherit"
                            >
                                {message}
                            </Typography>

                            {!!subMessages?.length && (
                                <ul className={styles.subMessages}>
                                    {subMessages.map((subMessage) => (
                                        <li key={subMessage}>
                                            {subMessage}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </Alert>
            </Slide>
        </div>
    );
}

interface PageMessageContextValue {
    message: string | undefined;
    subMessages: string[] | undefined;
    severity: AlertProps['severity'];
    dialogOpen: boolean;
    setDialogOpen: (isOpen: boolean) => void;
    setPageMessage: (
        message: string | undefined,
        severity?: AlertProps['severity'],
        subMessages?: string[]
    ) => void;
}

export const PageMessageContext = createContext<PageMessageContextValue>({
    message: undefined,
    subMessages: undefined,
    severity: undefined,
    dialogOpen: false,
    setDialogOpen: () => {},
    setPageMessage: () => {}
});

export function usePageMessageValue(): PageMessageContextValue {
    const [ message, setMessage ] = useState<PageMessageContextValue['message']>(undefined);
    const [ subMessages, setSubMessages ] = useState<PageMessageContextValue['subMessages']>(undefined);
    const [ severity, setSeverity ] = useState<PageMessageContextValue['severity']>();
    const [ dialogOpen, setDialogOpen ] = useState<PageMessageContextValue['dialogOpen']>(false);

    const setPageMessage = useMemo(() => (
        newMessage: typeof message,
        newSeverity: typeof severity,
        newSubMessages: typeof subMessages
    ) => {
        setMessage(newMessage);
        setSubMessages(newSubMessages);

        if (newSeverity) {
            setSeverity(newSeverity);
        }
    }, []);

    return useMemo(() => ({
        message,
        subMessages,
        severity,
        dialogOpen,
        setDialogOpen,
        setPageMessage
    }), [
        message, subMessages, severity, dialogOpen, setPageMessage
    ]);
}

const AUTO_HIDE_DELAY = 5000;

export function usePageMessage(delay: number = 250) {
    const { setPageMessage: setPageMessageContext } = useContext(PageMessageContext);
    const pendingHideTimeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => (
        () => {
            if (pendingHideTimeoutRef.current) {
                clearTimeout(pendingHideTimeoutRef.current);
            }

            setPageMessageContext(undefined);
        }
    ), [ setPageMessageContext ]);

    const setPageMessage = useCallback((
        message: string,
        severity: PageMessageContextValue['severity'],
        subMessages: PageMessageContextValue['subMessages']
    ) => {
        setPageMessageContext(message, severity, subMessages);

        if (pendingHideTimeoutRef.current) {
            clearTimeout(pendingHideTimeoutRef.current);
        }

        if (severity === 'success' || severity === 'info') {
            pendingHideTimeoutRef.current = setTimeout(() => {
                setPageMessageContext(undefined);
                pendingHideTimeoutRef.current = undefined;
            }, AUTO_HIDE_DELAY);
        }
    }, [ setPageMessageContext ]);

    const setMessageWithDelay = useCallback((
        message: string,
        severity: PageMessageContextValue['severity'],
        subMessages: PageMessageContextValue['subMessages']
    ) => {
        if (delay) {
            setTimeout(() => setPageMessage(message, severity, subMessages), delay);
        } else {
            setPageMessage(message, severity, subMessages);
        }
    }, [ setPageMessage, delay ]);

    const handleApiError = useCallback((message: string, error: unknown) => {
        const isAnApiError = isApiError(error);
        const subMessages = isAnApiError
            ? isValidationApiError(error)
                ? Object.values(error.errors).flat()
                : [ error.detail || 'An unexpected error occurred' ]
            : [ 'An unexpected error occurred' ];

        if (isAnApiError) {
            message = error.title;
        }

        setMessageWithDelay(message, 'error', subMessages);
    }, [ setMessageWithDelay ]);

    return useMemo(() => ({
        success: (message: string, subMessages?: string[]) => (
            setMessageWithDelay(message, 'success', subMessages)
        ),
        error: (message: string, subMessages?: string[]) => (
            setMessageWithDelay(message, 'error', subMessages)
        ),
        info: (message: string, subMessages?: string[]) => (
            setMessageWithDelay(message, 'info', subMessages)
        ),
        handleApiError,
        useHandleApiError: (message: string, error: unknown) => {
            useEffect(() => {
                if (error) {
                    handleApiError(message, error);
                }
            }, [ message, error ]);
        }
    }), [ setMessageWithDelay, handleApiError ]);
}
